import { useState } from "react";
import type { FC } from "react";
import Button from "react-bootstrap/Button";

import { AbilityScore, BookmarkButton, Lightbox } from "components";
import { SolutionModal } from "features";

import type { TSolutionVariant, IAssignmentResult, IOption } from "store/slices/exercise";

import { getAssignmentModalExerciseByType } from "utils/getAssignmentByType";
import { getTextByAssignmentType } from "utils/formatTextForAnalysis";
import { getTextForSelfEvaluationOption } from "utils/getTextForSelfEvaluationOption";
import { isSingleAbilityScore } from "utils/valueTranslators";
import { getAbilityScoreOptions } from "utils/changeAbilityScore";

import type { UserAssignmentForExerciseFragment } from "api/generated";

interface Props {
  result: IAssignmentResult<IOption | string>;

  assignment: UserAssignmentForExerciseFragment;
  isOpen: boolean;
  isSelfEvalDisabled?: boolean;
  onClose: () => void;
}

const ResultModalExercise: FC<Props> = ({ assignment, result, isOpen, isSelfEvalDisabled, onClose }) => {
  const assignmentDetail = assignment.assignment;
  if (assignmentDetail === null || assignmentDetail === undefined) return null;

  const isSelfEvaluation = assignment.assignment?.scoring?.scoringMethod.scoringMethod === "sebeopravení";

  const [questionAnalysisShown, setQuestionAnalysisShown] = useState<number | null>(null);

  const toggleModals = (questionAnalysisShown: number | null) => {
    setQuestionAnalysisShown(questionAnalysisShown);
  };

  const closeSolutionModal = () => {
    setQuestionAnalysisShown(null);
  };

  const getAnswerText = () => {
    if (questionAnalysisShown === null) return "";

    if (assignment.assignment?.scoring?.scoringMethod.scoringMethod === "sebeopravení") {
      return getTextForSelfEvaluationOption(result.answersVariants?.[questionAnalysisShown ?? 0] ?? "danger");
    }

    const type = assignment.assignment?.assignment?.assignmentType.type;
    if (typeof type !== "string" || result.answers === undefined) return "";

    return getTextByAssignmentType(type, result.answers[questionAnalysisShown]);
  };

  const getAnswerType = (): TSolutionVariant => {
    if (result.answersVariants === undefined) return "danger";

    const type = result.answersVariants?.[questionAnalysisShown ?? 0];

    return type ?? "danger";
  };

  const isCorrect = result.status === "correct" || result.status === "partial";
  const selfEvalOptions = getAbilityScoreOptions(isSelfEvaluation, isCorrect ? "success" : null);

  return (
    <>
      <Lightbox
        show={isOpen && questionAnalysisShown === null}
        onHide={onClose}
        className={"modal--exercise"}
        size={"lg"}
        // Obsah se skládá dynamicky podle typu úlohy, žádný stabilní nadpis tu není —
        // jméno je proto neviditelné. Rozhodnuto se zadavatelem 31. 8. 2026. [TD-344]
        ariaLabel="Vyhodnocení úlohy"
        footerClassName={"modal-footer--yellow-bg"}
        footer={
          <div className="mw-sm">
            {isSingleAbilityScore(assignment.assignment) && selfEvalOptions !== null ? (
              <AbilityScore
                isSelfEvaluated={isSelfEvaluation}
                options={selfEvalOptions}
                selectedValue={Array.isArray(result.abilityScore) ? result.abilityScore[0] : result.abilityScore}
                onValueSelect={() => {}}
                index={0}
                isSelfEvalDisabled={isSelfEvalDisabled}
              />
            ) : null}

            <div className="modal-footer-btn-stretch">
              <BookmarkButton userAssignmentId={assignment.id} />
              {/* TODO: translate */}
              <Button variant={"outline-secondary-darker"} onClick={onClose}>
                Zavřít
              </Button>
            </div>
          </div>
        }
      >
        <div className="exercise">
          <div className="exercise__body">
            {getAssignmentModalExerciseByType(
              assignment.assignment?.assignment?.assignmentType.type,
              assignment,
              result,
              toggleModals,
            )}
          </div>
        </div>
      </Lightbox>

      <SolutionModal
        isShown={isOpen && questionAnalysisShown !== null}
        assignment={assignmentDetail}
        questionIndex={questionAnalysisShown ?? 0}
        answerType={getAnswerType()}
        toggleModal={closeSolutionModal}
        isGoBackShown={true}
        userAssignmentId={assignment.id}
      >
        {getAnswerText()}
      </SolutionModal>
    </>
  );
};

export default ResultModalExercise;
